import React, { FC, useMemo, useRef, useState } from 'react';

import {
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';
import { Flex, theme, Typography } from 'antd';
import { createPortal } from 'react-dom';

import { useNavigate } from 'react-router-dom';

import { ArtifactItemSchema, RetrieveOrderSchema, RetrieveProcessSchema } from '@app/models/orders';

import { BoardColumn } from './components/BoardColumn';
import { StageStatusesMenu } from './components/StageStatusesMenu';
import { ITask, TaskCard } from './components/TaskCard';
import { coordinateGetter } from './multipleContainersKeyboardPreset';
import { StageContainer } from './styles';
import { getBoardView, hasDraggableData, mapOrderToTask } from './utils';

interface IProps {
  isLoading: boolean;
  boardData: RetrieveProcessSchema;
  orders: RetrieveOrderSchema[];
  onChangeStatus: (order_uid: string, status_uid: string, version: string) => void;
}

export const KanbanBoard: FC<IProps> = ({ orders, boardData, onChangeStatus }) => {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const warehouseFlowArtifactUid = boardData?.order_structure?.items?.find(
    (item): item is ArtifactItemSchema => item.type === 'artifact' && item.data.type === 'warehouse_flow'
  )?.data?.uid;

  const [tasks, setTasks] = useState<ITask[]>(
    orders?.map((order) => mapOrderToTask(order, warehouseFlowArtifactUid)) || []
  );
  const [activeTask, setActiveTask] = useState<ITask | null>(null);
  const originColumnIdRef = useRef<string | null>(null);
  const targetColumnIdRef = useRef<string | null>(null);

  React.useEffect(() => {
    setTasks(orders?.map((order) => mapOrderToTask(order, warehouseFlowArtifactUid)));
  }, [orders, warehouseFlowArtifactUid]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 0.1 } }),
    useSensor(TouchSensor, { activationConstraint: { distance: 0.1 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: coordinateGetter,
    })
  );

  const handleCardClick = (uid: string) => {
    navigate(uid);
  };

  function onDragStart(event: DragStartEvent) {
    if (!hasDraggableData(event.active)) return;
    const data = event.active.data.current;

    if (data?.type === 'Task') {
      setActiveTask(data.task);
      originColumnIdRef.current = data.task.columnId;
      targetColumnIdRef.current = data.task.columnId;
      return;
    }
  }

  function onDragEnd(event: DragEndEvent) {
    const activeData = event.active.data.current;
    const task = activeData?.type === 'Task' ? activeData.task : null;
    const originColumnId = originColumnIdRef.current;
    const targetColumnId = event.over ? targetColumnIdRef.current : null;

    if (
      task &&
      originColumnId &&
      targetColumnId &&
      originColumnId !== targetColumnId &&
      task.available_statuses_uids.includes(targetColumnId)
    ) {
      onChangeStatus(task.id, targetColumnId, task.version);
    }

    setActiveTask(null);
    originColumnIdRef.current = null;
    targetColumnIdRef.current = null;
  }

  function onDragOver(event: DragOverEvent) {
    const { active, over } = event;

    if (!over) {
      targetColumnIdRef.current = null;
      return;
    }

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    if (!hasDraggableData(active) || !hasDraggableData(over)) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    const isActiveATask = activeData?.type === 'Task';
    const isOverATask = overData?.type === 'Task';

    if (isActiveATask && isOverATask) {
      const overIndex = tasks.findIndex((t) => t.id === overId);
      const overTask = tasks[overIndex];
      // over column not available
      if (!overTask || !activeData.task.available_statuses_uids.includes(overTask.columnId)) {
        targetColumnIdRef.current = null;
        return;
      }

      targetColumnIdRef.current = overTask.columnId;
      setTasks((tasks) => {
        const activeIndex = tasks.findIndex((t) => t.id === activeId);
        const overIndex = tasks.findIndex((t) => t.id === overId);
        const activeTask = tasks[activeIndex];
        const overTask = tasks[overIndex];
        if (activeTask && overTask && activeTask.columnId !== overTask.columnId) {
          const updatedTasks = tasks.map((task) =>
            task.id === activeId ? { ...task, columnId: overTask.columnId } : task
          );
          return arrayMove(updatedTasks, activeIndex, overIndex - 1);
        }

        return arrayMove(tasks, activeIndex, overIndex);
      });
    }

    const isOverAColumn = overData?.type === 'Column';

    // Im dropping a Task over a column
    if (isActiveATask && isOverAColumn) {
      // over column not available
      const targetColumnId = String(overId);
      if (!activeData.task.available_statuses_uids.includes(targetColumnId)) {
        targetColumnIdRef.current = null;
        return;
      }
      targetColumnIdRef.current = targetColumnId;
      setTasks((tasks) => {
        const activeIndex = tasks.findIndex((t) => t.id === activeId);
        const activeTask = tasks[activeIndex];
        if (activeTask) {
          return tasks.map((task) => (task.id === activeId ? { ...task, columnId: targetColumnId } : task));
        }
        return tasks;
      });
    }
  }

  const boardView = useMemo(() => getBoardView(boardData, tasks), [boardData, tasks]);

  return (
    <div className="kanban">
      <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragOver={onDragOver}>
        <Flex vertical={false} wrap={false} gap={10} style={{ position: 'relative' }}>
          <SortableContext items={boardView.columnsId}>
            {boardView.stages.map((stage) => (
              <Flex key={stage.uid} align="start">
                <Flex vertical className="stage-wrapper" style={{ height: '100%' }}>
                  <StageContainer align="center" justify="space-between" $colorBgBase={token.colorBgBase}>
                    <Typography.Title level={5} style={{ margin: 0 }} type="secondary">
                      {stage.name} {stage.total > 0 && `(${stage.total})`}
                    </Typography.Title>
                    {stage.moduleUid && stage.statuses?.length > 0 && (
                      <StageStatusesMenu
                        statuses={stage.statuses}
                        hiddenStatusUids={stage.hiddenStatusUids}
                        moduleUid={stage.moduleUid}
                        processUid={boardData.uid}
                      />
                    )}
                  </StageContainer>
                  <Flex gap={12} className="status-wrapper" style={{ height: '100%' }}>
                    {stage.visibleStatuses.map((col) => (
                      <BoardColumn
                        isDisabled={
                          activeTask ? !activeTask?.available_statuses_uids?.some((el) => el === col.uid) : false
                        }
                        key={col.uid}
                        column={{ title: col.name, id: col.uid || '', color: col.color }}
                        tasks={col.tasks}
                        onCardClick={handleCardClick}
                      />
                    ))}
                  </Flex>
                </Flex>
              </Flex>
            ))}
          </SortableContext>
        </Flex>

        {'document' in window &&
          createPortal(
            <DragOverlay>{activeTask && <TaskCard task={activeTask} isOverlay />}</DragOverlay>,
            document.body
          )}
      </DndContext>
    </div>
  );
};
